/**
 * VehicleMpgChart — Section 7 (iter-130).
 * Monthly MPG bar chart per vehicle. Bars turn red when the month's MPG
 * dropped more than 20% below its own 3-month trailing average. Meant
 * to be mounted inside both the Procurement and Finance panels; the
 * caller supplies which vehicle_id (or a picker above).
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { Gauge, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VehicleMpgChart({ testId = 'vehicle-mpg-chart', vehicleId: pinnedId = null, hidePicker = false }) {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(pinnedId || '');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (pinnedId) { setVehicleId(pinnedId); return; }
    (async () => {
      const r = await fetch(`${API}/api/procurement/vehicles`, { credentials: 'include' });
      if (r.ok) {
        const list = await r.json();
        setVehicles(list);
        if (list.length && !vehicleId) setVehicleId(list[0].vehicle_id);
      }
    })();
  }, [vehicleId, pinnedId]);

  useEffect(() => {
    if (!vehicleId) return;
    (async () => {
      const r = await fetch(`${API}/api/procurement/vehicles/${vehicleId}/mpg?months=6`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    })();
  }, [vehicleId]);

  const rows = data?.months || [];
  const flaggedCount = rows.filter((r) => r.flagged).length;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <Gauge className="h-5 w-5" style={{ color: '#0ea5e9' }} />
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Monthly MPG Trend
            </h3>
            <p className="text-[11px] text-zinc-500">
              Miles from inspection deltas · gallons from fuel receipts · red = &gt;20% drop vs 3-month avg
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {flaggedCount > 0 && (
            <Badge className="bg-red-600 text-white text-xs" data-testid="mpg-flagged-count">
              <AlertTriangle className="h-3 w-3 mr-1" /> {flaggedCount} flagged
            </Badge>
          )}
          {!hidePicker && (
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-56" data-testid="mpg-vehicle-pick"><SelectValue placeholder="Pick vehicle" /></SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => (
                  <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500 py-8 text-center">No MPG data available yet.</p>
      ) : (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} label={{ value: 'MPG', angle: -90, position: 'insideLeft', fill: '#a1a1aa', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', color: '#fafafa', fontSize: 12 }}
                formatter={(v, k, p) => {
                  const row = p?.payload || {};
                  return [`${v ?? '—'}`, row.flagged ? 'MPG (flagged)' : 'MPG'];
                }}
              />
              <Bar dataKey="mpg" radius={[4, 4, 0, 0]}>
                {rows.map((r) => (
                  <Cell key={r.month} fill={r.flagged ? '#ef4444' : '#22c55e'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-6 gap-2 text-[11px]">
        {rows.map((r) => (
          <div key={r.month} className={`p-2 rounded border ${r.flagged ? 'border-red-500 bg-red-950/40' : 'border-zinc-800 bg-zinc-950/50'}`} data-testid={`mpg-cell-${r.month}`}>
            <p className="text-zinc-500 uppercase tracking-widest">{r.month}</p>
            <p className={`font-semibold ${r.flagged ? 'text-red-400' : 'text-white'}`}>{r.mpg ?? '—'} MPG</p>
            <p className="text-zinc-500">{r.gallons.toFixed(1)} gal · {r.miles?.toLocaleString?.() ?? '—'} mi</p>
          </div>
        ))}
      </div>
    </Card>
  );
}
